<template>
  <!-- prettier-ignore -->
  <the-modal
    v-model="isSaveNudgeOpen"
    locked
    name="saveNudge">

    <Modal.Header
      :text="$t('basket.saveNudge.saveForLater')"
    />

    <Form.Form
      v-unblu-protected
      class="screen-form form"
      name="save_nudge"
      tracking-disabled
      :validator="v"
      @cancel="close"
      @submit="submit">
      <Modal.Content>
        <the-typography
          type="heading05"
          v-text="$t('basket.modal.basketScreenModalText')"
        />

        <Form.Input
          v-model="formData.email"
          class="form__input"
          required
          name="email"
          type="email"
          theme="dark"
          :placeholder="$t('person.email')"
          :title="$t('person.email')"
          :validate="v.email"
        />

        <the-typography
          type="heading05"
          v-text="$t('basket.modal.basketScreenDataProtection')"
        />
        <the-typography
          type="bodyLargeLong"
          v-text="$t('basket.modal.basketScreenDataProtectionText')"
        />
      </Modal.Content>

      <Modal.Footer>
        <template #primary>
          <Form.Button
            class="form__button"
            primary
            has-loading-indicator
            type="submit"
            :text="$t('form.save')"
          />
        </template>

        <template #secondary>
          <Form.Button
            class="form__button"
            secondary
            :text="$t('form.cancel')"
            @click="close"
          />
        </template>
      </Modal.Footer>

    </Form.Form>
  </the-modal>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { loading$, events$ } from '@/services'

import { useBasket, useToast, useProduct, useStatus } from '@/hooks'

import * as Modal from '@/components/Modal'
import * as Form from '@/components/Form'

import { ROUTE_INDEX, SAVE_NUDGE_DELAY } from '@/config/constants'
import { EVENT_BASKET, EVENT_OVP, SOURCE } from '@/config/events'
import { email } from '@/validations/components'

// Hooks
const { basket, updateOvpBasket } = useBasket()
const { addToast, removeToast } = useToast()
const { hasSelectedProducts } = useProduct()
const { isOnboardingSecured } = useStatus()
const { t } = useI18n()
const route = useRoute()

// Data
const formData = reactive({
  email: undefined,
})
const v = useVuelidate(
  {
    email,
  },
  formData,
  { $lazy: true }
)
const isSaveNudgeOpen = ref(false)
const isSaveNudgeEnabled = parseInt(import.meta.env.VITE_FEATURE_SAVE_NUDGE) === 1
let nudgeInstance = false

// Methods
function close(trackClose = true) {
  isSaveNudgeOpen.value = false

  if (nudgeInstance) removeToast(nudgeInstance)
  if (trackClose) events$.emit(EVENT_OVP.NUDGE_CLOSE)
}

function registerListener() {
  if (isSaveNudgeEnabled && !basket.email && !hasSelectedProducts.value) {
    events$.once(EVENT_BASKET.WIDGET_CLOSE, showNudging)
  }
}

function showNudging() {
  if (isSaveNudgeEnabled && !basket.email) {
    window.setTimeout(() => {
      // don't show on basket and later screens
      if (route.meta.index >= ROUTE_INDEX.BASKET) {
        return
      }

      nudgeInstance = addToast({
        isPermanent: true,
        text: t('basket.modal.basketSavingTitle'),
        actions: [
          {
            label: t('form.save'),
            callback: toastId => {
              removeToast(toastId)
              isSaveNudgeOpen.value = true
            },
          },
        ],
      })
    }, SAVE_NUDGE_DELAY)
  }
}

async function submit() {
  const offerUpdate = isOnboardingSecured.value

  loading$.start()

  const payload = Object.assign({}, formData)
  try {
    const __basket = await updateOvpBasket(payload, {}, { emailType: 'OFFER_REENTRY_WITHOUT_EGATE' })

    close(false)
    events$.emit(EVENT_BASKET.SAVED, {
      basket: __basket,
      source: SOURCE.SAVE_NUDGE,
      update: offerUpdate,
    })

    addToast({
      text: t('basket.modal.basketSaved'),
    })
    loading$.end()
  } catch {
    loading$.failed()
  }
}

// Watchers
const stopWatching = watch(route, it => {
  if (it.meta.index >= ROUTE_INDEX.BASKET) {
    close(false)
    stopWatching()
  }
})

// Lifecycle
onMounted(() => {
  registerListener()
})
</script>

<style name="animations" scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-up-leave-active,
.slide-up-enter {
  opacity: 0;
  height: 0;
  transform: translate(0, -2em);
}
</style>

<style name="mobile" scoped>
.save-nudge-wrapper {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  margin: 0 auto;
  z-index: 811; /* must have a z-index since it's included in the new navigation */
  width: 100%;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
  color: var(--c-primary-neutral-1);

  .save-nudge {
    position: relative;
    padding: 15px 15px 0 15px;
    background-color: var(--c-primary-neutral-2);
    font-size: 15px;
    line-height: 20px;

    .title {
      font-weight: bold;
      margin-bottom: 10px;
    }

    .screen-form {
      .form__input {
        flex-grow: 1;
        padding: 15px 0;
      }

      .form__button {
        min-width: 180px;
        margin-bottom: 24px;
      }
    }
  }
}
.save-nudge__close {
  position: absolute;
  top: 5px;
  right: 5px;
  color: var(--c-primary-color-3);
  font-size: 24px;
  cursor: pointer;
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .save-nudge-wrapper {
    width: 640px;

    .save-nudge {
      padding: 20px 20px 0 20px;

      .screen-form {
        margin: 0 auto;
        display: flex;
        align-items: center;
        align-content: space-between;
        padding-top: 10px;
        padding-bottom: 20px;

        .form__input {
          padding: 0 20px 0 0;
        }

        .form__button {
          align-self: flex-start;
        }
      }
    }
  }
}
</style>
